import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { FormGroup } from '@angular/forms';

import { BaseInputComponent } from '../base-input.component';
import { FormOption } from '../form-option';
import { TypedFormGroup } from '@feature/forms/form-model';
import { ReadOnlyUserService } from '@core/read-only-user/read-only-user.service';
import { Subscription } from 'rxjs';

export type Layout = 'row' | 'column';

@Component({
  selector: 'wai-form-selection-list',
  templateUrl: './form-selection-list.component.html',
  styleUrls: ['./form-selection-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormSelectionListComponent extends BaseInputComponent implements OnChanges, AfterViewInit, OnDestroy {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  @Input() public hiddenOptions = [];
  @Input() public isMultiselect = false;
  @Input() public isDisabled?: boolean;
  @Input() public label: string;
  @Input() public layout: Layout = 'row';
  @Input() public options: FormOption[];

  private readOnlyUserService = inject(ReadOnlyUserService);
  private controlStatusSubscription?: Subscription;

  public get layoutGap(): string {
    if (this.layout === 'column') {
      return '0';
    }

    return '32px';
  }

  public ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if ((changes['isDisabled'] || changes['control']) && this.control) {
      this.updateDisabledState();
    }
  }

  public ngAfterViewInit(): void {
    this.updateDisabledState();

    // React to parent form enable/disable changes
    if (this.control) {
      this.controlStatusSubscription = this.control.statusChanges.subscribe(() => {
        // Only auto-update when isDisabled is not explicitly set
        if (this.isDisabled === undefined || this.isDisabled === null) {
          this.updateDisabledState();
        }
      });
    }
  }

  private updateDisabledState(): void {
    if (!this.control) {
      return;
    }

    // Priority 1: If isDisabled is explicitly set, use that value
    if (this.isDisabled !== undefined && this.isDisabled !== null) {
      if (this.isDisabled) {
        this.control.disable({ emitEvent: false });
      } else {
        const isReadOnly = this.readOnlyUserService.getIsReadOnlyUser();
        const isFormDisabled = this.isParentFormDisabled();
        if (!isReadOnly && !isFormDisabled) {
          this.control.enable({ emitEvent: false });
        }
      }
      return;
    }

    // Priority 2: Read-only user status
    if (this.readOnlyUserService.getIsReadOnlyUser()) {
      this.control.disable({ emitEvent: false });
      return;
    }

    // Priority 3: Respect parent form disabled state
    if (this.isParentFormDisabled()) {
      this.control.disable({ emitEvent: false });
      return;
    }

    // Otherwise enable
    this.control.enable({ emitEvent: false });
  }

  private isParentFormDisabled(): boolean {
    if (!this.control?.parent) {
      return false;
    }

    const parent = this.control.parent;
    if (parent instanceof FormGroup) {
      return parent.disabled;
    }

    let currentParent: any = parent;
    while (currentParent) {
      if (currentParent instanceof FormGroup && currentParent.disabled) {
        return true;
      }
      currentParent = currentParent.parent;
    }

    return false;
  }

  public ngOnDestroy(): void {
    if (this.controlStatusSubscription) {
      this.controlStatusSubscription.unsubscribe();
    }
  }
}
